import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { BackLink } from '~/components/ui/NavLinks';
import { PageTitle } from '~/components/ui/Page';
import { Table } from '~/components/ui/Table';
import styles from '~/styles/button.module.css';
import { CreateLessonButton } from './CreateLessonButton';

type LessonAdminSummary = components['schemas']['LessonAdminSummary'];

export const Route = createFileRoute('/_admin/lessons/')({
  validateSearch: (s: Record<string, unknown>) => ({
    course: Number(s.course),
    version: Number(s.version),
  }),
  component: LessonsList,
});

const columnHelper = createColumnHelper<LessonAdminSummary>();

const columns = [
  columnHelper.accessor('order', { header: 'Order' }),
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('title', { header: 'Title' }),
  columnHelper.accessor('status', { header: 'Status' }),
];

function LessonsList() {
  const { course, version } = Route.useSearch();
  const [page, setPage] = useState(0);

  // Admin endpoint: all statuses (the student course_lessons is gated to a
  // fully published path).
  const { data, isLoading, error } = api.adminListLessons.useQuery({
    path: { course_id: course },
    query: { version, limit: 10, offset: 10 * page },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <Message>Loading lessons...</Message>;
  if (error) return <Message $error>Error loading lessons</Message>;

  return (
    <div>
      <BackLink>
        <Link
          to="/courses/$courseId/$version"
          params={{ courseId: String(course), version: String(version) }}
        >
          &larr; Back to course
        </Link>
      </BackLink>
      <Header>
        <PageTitle>Lessons</PageTitle>
        <CreateLessonButton courseId={course} version={version} />
      </Header>
      <Toolbar>
        <div style={{ flexGrow: 1 }}>Page {page + 1}</div>
        <Button
          disabled={page === 0}
          className={styles.Button}
          onClick={() => setPage((v) => v - 1)}
        >
          Previous
        </Button>
        <Button className={styles.Button} onClick={() => setPage((v) => v + 1)}>
          Next
        </Button>
      </Toolbar>

      <Table
        table={table}
        to="/lessons/$lessonId"
        getParams={(row) => ({ lessonId: String(row.id) })}
      />
    </div>
  );
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--component-margin);
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  margin: var(--component-margin);
  gap: var(--component-margin);
`;

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;
